import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Button } from "reactstrap";

import Icon from "shared/react_components/MaterialIcon";
import Timestamp from "shared/react_components/Timestamp";
import LoaderOverlay from "shared/react_components/LoaderOverlay";

import PlaybackRateDropdown from "./PlaybackRateDropdown";
import DefaultTrack from "./DefaultTrack";
import EventsTrack from "./EventsTrack";
import { playbackButton, playbackIcon, Progress, separator, audioPlayer, loading, addCommentButton } from "./AudioPlayer.module.scss";

const PLAYBACK_RATE_LOCAL_STORAGE_KEY = "invocaAudioPlayerPlaybackRate";

function clientXPercent({ width, clientX, x, left }) {
  return Math.min(width, Math.max(0, clientX - (x || left))) / width;
}

const AudioPlayer = ({ src, remotePaused, onPlay, onPause, onPositionChange, onAddComment, showPlaybackRate, showQuickNav, audioJumpPosition, onAudioPositionJumped, children }) => {
  const [paused, setPaused] = useState(true);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState((showPlaybackRate && parseFloat(window.localStorage.getItem(PLAYBACK_RATE_LOCAL_STORAGE_KEY))) || 1.0);
  const [audioDataLoading, setAudioDataLoading] = useState(true);

  const player = useRef();
  const progress = useRef();

  const [width, setWidth] = useState(0);
  const resizeWidth = useCallback(() => setWidth(progress.current ? progress.current.getBoundingClientRect().width : 0), [progress]);

  useEffect(() => {
    setPaused(remotePaused);
    remotePaused ? pause() : play();
  }, [remotePaused]);

  useEffect(() => {
    window.addEventListener('resize', resizeWidth);
    return () => window.removeEventListener('resize', resizeWidth);
  });

  useEffect(() => resizeWidth(), [progress]);

  const onDurationChange = useCallback(({ target: targetPlayer }) => setDuration(targetPlayer.duration || 0), []);
  const onTimeUpdate = useCallback(({ target: targetPlayer }) => setPosition(targetPlayer.currentTime || 0), []);
  const onPlayPauseChange = useCallback(({ target: targetPlayer }) => setPaused(targetPlayer.paused), []);

  useEffect(() => onPositionChange(position), [position]);

  useEffect(() => {
    // Check on every useEffect since re-renders may cause the current player reference to change
    if (player.current.playbackRate !== playbackRate) {
      player.current.playbackRate = playbackRate;
    }
  });

  const play = useCallback(() => {
    onPlay();
    player.current.play();
  }, [onPlay, player]);

  useEffect(() => {
    if (typeof audioJumpPosition === "number") {
      setPosition(audioJumpPosition);
      if (!isNaN(player.current.duration)) { // Make sure audio has loaded
        player.current.currentTime = audioJumpPosition;
      }
      if (paused) {
        play();
      }
      onAudioPositionJumped && onAudioPositionJumped();
    }
  }, [audioJumpPosition]);

  const pause = useCallback(() => {
    onPause();
    player.current.pause();
  }, [onPause, player]);

  const onPositionConstChange = useCallback((diff) => {
    const newPosition = Math.min(duration, Math.max(0, position + diff));
    setPosition(newPosition);
    player.current.currentTime = newPosition;
  }, [player, position, duration]);

  const onPositionPercentChange = useCallback((percent) => {
    player.current.currentTime = percent * duration;
    setPosition(player.current.currentTime);
  }, [player, duration]);

  const onProgressClick = useCallback(({ clientX }) => {
    const { x, left } = progress.current.getBoundingClientRect();
    onPositionPercentChange(clientXPercent({ width, clientX, x, left }));
  }, [width, progress, onPositionPercentChange]);

  const seek = useCallback(() => {
    const { x, left } = progress.current.getBoundingClientRect();

    // left needed for IE11 support
    const trackMouse = ({ clientX }) => onPositionPercentChange(clientXPercent({ width, clientX, x, left }));

    const removeEvents = () => {
      document.removeEventListener("mousemove", trackMouse);
      document.removeEventListener("mouseup", removeEvents);
    };

    document.addEventListener("mousemove", trackMouse);
    document.addEventListener("mouseup", removeEvents);
  }, [width, progress, position, duration, onPositionPercentChange]);

  const progressPercent = useCallback((seconds) => {
    return duration > 0 ? (seconds / duration) * 100 : 0;
  }, [duration]);

  const progressPixel = useCallback((seconds) => {
    return duration > 0 ? ((seconds / duration) * width) : 0;
  }, [duration, width]);

  const onPlaybackRateChange = (playbackRate) => {
    setPlaybackRate(playbackRate);
    window.localStorage.setItem(PLAYBACK_RATE_LOCAL_STORAGE_KEY, playbackRate);
  };

  return (
    <LoaderOverlay loading={audioDataLoading}>
      <div className={audioPlayer}>
        <audio src={src} ref={player} onDurationChange={onDurationChange} onTimeUpdate={onTimeUpdate}
          onPlay={onPlayPauseChange} onPause={onPlayPauseChange} onLoadedData={() => setAudioDataLoading(false)}
        />
        <div className="d-flex align-items-center">
          {showQuickNav &&
            <button className={playbackButton} onClick={() => onPositionConstChange(-10)} data-test-id="quick-nav-rewind">
              <Icon name="replay_10" className={playbackIcon} />
            </button>}
          <button onClick={paused ? play : pause} className={playbackButton} data-test-id="play-pause">
            <Icon name={paused ? "play_circle_outline" : "pause_circle_outline"} className={playbackIcon} />
          </button>
          {showQuickNav &&
            <button className={playbackButton} onClick={() => onPositionConstChange(10)} data-test-id="quick-nav-forward">
              <Icon name="forward_10" className={playbackIcon} />
            </button>}

          {showPlaybackRate &&
            <div className={separator}>
              <PlaybackRateDropdown playbackRate={playbackRate} onChange={onPlaybackRateChange} />
            </div>}

          <Timestamp className="text-right pr-3" seconds={position} trimLeadingZeros />
          <div className={classNames(Progress, audioDataLoading ? loading : "")} ref={progress} onMouseDown={seek} onClick={onProgressClick}>
            {(children && children({ position, onSeek: seek, progressPercent, progressPixel })) ||
              <DefaultTrack position={position} progressPixel={progressPixel} onSeek={seek}/>}
          </div>
          <Timestamp className="text-left pl-3" seconds={duration} trimLeadingZeros />
        </div>
        {onAddComment &&
          <Button
            className={addCommentButton}
            outline
            color="link"
            onClick={() => onAddComment({ position })}
            data-test-id="add-comment-button"
          >
            <Icon name="chat"/> Add comment at {<Timestamp seconds={position} trimLeadingZeros />}
          </Button>
        }
      </div>
    </LoaderOverlay>
  );
};

AudioPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  remotePaused: PropTypes.bool,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onPositionChange: PropTypes.func,
  onAddComment: PropTypes.func,
  showPlaybackRate: PropTypes.bool,
  showQuickNav: PropTypes.bool,
  audioJumpPosition: PropTypes.number,
  onAudioPositionJumped: PropTypes.func,
  children: PropTypes.func
};

AudioPlayer.defaultProps = {
  remotePaused: true,
  onPlay: () => {},
  onPause: () => {},
  showPlaybackRate: false,
  onPositionChange: () => {},
  showQuickNav: false
};

export { EventsTrack };

export default AudioPlayer;
